import { useEffect, useState } from 'react';
import { partnerErrorMessage } from '../state/partnerError';
import { identityGateway } from '../../identity/services';
import type { MfaEnrollmentResponse } from '../../identity/types';
import { useStepUpGuard } from '../../security/components/useStepUpGuard';
import { AuthenticatorSection } from '../../security/pages/SecurityWorkspacePage';
import { FirmStatusBadge } from '../components/StatusBadge';
import { Unavailable } from '../components/Unavailable';
import { partnerGateway } from '../services';
import { useLoad } from '../state/useLoad';
import { usePartner } from '../state/usePartner';

/** Read-only for now: editing the profile, branding and contacts is not built. The EIN is shown as its last four digits only, because that is all the system keeps. */
export function PartnerProfilePage() {
  const { membership } = usePartner();
  const org = useLoad((signal) => partnerGateway.getOrganization(membership.partnerOrganizationId, signal), membership.partnerOrganizationId, 'The firm profile could not be loaded.');
  const o = org.data;

  return (
    <>
      <h1>Partner profile</h1>
      {org.error && <p role="alert" className="pp-error">{org.error}</p>}
      {org.loading && !o && <p className="pp-muted">Loading…</p>}
      {o && (
        <dl className="pp-card pp-facts pp-facts--wide">
          <div><dt>Legal business name</dt><dd>{o.legalName}</dd></div>
          {o.dba && <div><dt>Doing business as</dt><dd>{o.dba}</dd></div>}
          <div><dt>Firm type</dt><dd>{o.firmType.replace(/([a-z])([A-Z])/g, '$1 $2')}</dd></div>
          <div><dt>EIN</dt><dd>{o.einLast4 ? `Ending in ${o.einLast4}` : 'Not on file'}</dd></div>
          <div><dt>Status</dt><dd><FirmStatusBadge status={o.status} /></dd></div>
          <div><dt>Verification</dt><dd>{o.verificationStatus}</dd></div>
          <div><dt>Primary contact</dt><dd>{[o.primaryContactName, o.primaryContactEmail, o.primaryContactPhone].filter(Boolean).join(' · ') || 'Not on file'}</dd></div>
          <div><dt>Support contact</dt><dd>{o.supportEmail ?? 'Not on file'}</dd></div>
          <div><dt>States served</dt><dd>{o.statesServed.length > 0 ? o.statesServed.join(', ') : 'Not on file'}</dd></div>
        </dl>
      )}
      <Unavailable>Editing the profile, branding and notifications is not built yet. To correct something, contact Escochex.</Unavailable>
    </>
  );
}

export function PartnerSecurityPage() {
  const { guard, dialog } = useStepUpGuard();
  const [enrollment, setEnrollment] = useState<MfaEnrollmentResponse | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const list = await identityGateway.listMfaEnrollments();
      setEnrollment(list.find((e) => e.method === 'Authenticator' && e.isEnabled) ?? null);
    } catch (e) {
      setError(partnerErrorMessage(e, 'Your verification settings could not be loaded.'));
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the first load on mount, the same pattern SecurityWorkspacePage uses
    void load();
  }, []);

  return (
    <>
      <h1>Security</h1>
      <p>Every partner user must verify with an authenticator app each time they sign in. This cannot be turned off for a partner account.</p>
      {error && <p role="alert" className="pp-error">{error}</p>}
      {enrollment === undefined && !error && <p className="pp-muted">Loading…</p>}
      {enrollment !== undefined && (
        <section className="pp-card">
          <AuthenticatorSection enrollment={enrollment} onChanged={load} guard={guard} />
        </section>
      )}
      {dialog}
    </>
  );
}

export function PartnerAgreementsPage() {
  const { membership, can, reload } = usePartner();
  const org = useLoad((signal) => partnerGateway.getOrganization(membership.partnerOrganizationId, signal), membership.partnerOrganizationId, 'The agreement could not be loaded.');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const o = org.data;

  async function accept() {
    if (!o?.agreementVersion) return;
    setBusy(true);
    setError(null);
    try {
      await partnerGateway.acceptAgreement(membership.partnerOrganizationId, o.agreementVersion);
      await org.reload();
      await reload();
    } catch (e) {
      setError(partnerErrorMessage(e, 'The agreement could not be accepted.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Agreements</h1>
      {org.error && <p role="alert" className="pp-error">{org.error}</p>}
      {error && <p role="alert" className="pp-error">{error}</p>}
      {o && (
        <section className="pp-card">
          <dl className="pp-facts pp-facts--wide">
            <div><dt>Agreement version</dt><dd>{o.agreementVersion ?? 'None offered yet'}</dd></div>
            <div><dt>Accepted</dt><dd>{o.agreementAcceptedAtUtc ? new Date(o.agreementAcceptedAtUtc).toLocaleDateString() : 'Not yet'}</dd></div>
          </dl>
          {o.status === 'Approved' && can('partner.agreement.accept') && (
            <button type="button" className="pp-button pp-button--primary" disabled={busy} onClick={() => void accept()}>
              {busy ? 'Accepting…' : `Accept agreement ${o.agreementVersion ?? ''}`}
            </button>
          )}
          {o.status === 'Approved' && !can('partner.agreement.accept') && <p className="pp-muted">A Partner Owner must accept the agreement before your firm can start.</p>}
        </section>
      )}
      <Unavailable>The agreement text and commercial terms are published by Escochex and are not shown here yet. Revenue arrangements are not built.</Unavailable>
    </>
  );
}
